import {
  Button,
  Stack,
  Image,
  PasswordInput,
  Title,
  TextInput,
  Alert,
  Group,
  Anchor,
  Flex,
  Checkbox,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMutation } from '@tanstack/react-query';
import { registerCredentials } from '../../services/api';
import { IconInfoCircle } from '@tabler/icons-react';
import { LoginWrapper } from '../LoginWrapper';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageRoutes } from '../../constants/routes';
import { primaryColor } from '../../constants/theme';
import { useEffect, useState } from 'react';
import { TokenResponseModel } from '../../types/models';
import { LocalStorageKeys } from '../../constants/locationStorage';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../auth/AuthContext';
import { HaveAnAccountAlreadyText } from './SingleSignOnOptions';
export const SignUpLogin = ({ flowType }: SignUpLoginProps) => {
  const [rememberMe, setRememberMe] = useState(true);
  const form = useForm({
    initialValues: {
      email: '',
      password: '',
      confirmPassword: '',
    },
    validate: {
      email: (value) => (/^\S+@\S+$/.test(value) ? null : 'Invalid email'),
      password: (value) => {
        if (value.length < 6)
          return 'Password must be at least 6 characters long';
        if (!/[a-z]/.test(value))
          return 'Password must contain a lowercase letter';
        if (!/[A-Z]/.test(value))
          return 'Password must contain an uppercase letter';
        if (!/[0-9]/.test(value)) return 'Password must contain a number';
        return null;
      },
      confirmPassword: (value, values) =>
        flowType === LoginFlowType.SIGN_UP && value !== values.password
          ? 'Passwords do not match'
          : null,
    },
    onValuesChange: () => {
      resetRegister();
      resetLogin();
    },
  });

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getProfileAndRedirect } = useApp();
  const { loginWithPassword, setSession } = useAuth();
  const returnTo = searchParams.get('returnTo');

  useEffect(() => {
    if (returnTo) {
      localStorage.setItem(LocalStorageKeys.POST_LOGIN_REDIRECT, returnTo);
      return;
    }

    localStorage.removeItem(LocalStorageKeys.POST_LOGIN_REDIRECT);
  }, [returnTo]);

  const {
    mutate: register,
    isPending: isLoadingRegister,
    isError: isRegisterError,
    reset: resetRegister,
  } = useMutation({
    mutationFn: () => registerCredentials(form.values),
    onSuccess: async (response: TokenResponseModel) => {
      // After registration, login with the same credentials to get session
      try {
        const session = await loginWithPassword(
          form.values.email,
          form.values.password
        );
        setSession(session);
        handleAuthSuccess(
          session.emailConfirmed,
          session.profileConfirmed,
          session.accessToken
        );
      } catch (err) {
        console.error('Error logging in after registration:', err);
      }
    },
  });

  const {
    mutate: login,
    isPending: isLoadingLogin,
    isError: isLoginError,
    reset: resetLogin,
  } = useMutation({
    mutationFn: async () => {
      const session = await loginWithPassword(
        form.values.email,
        form.values.password,
        rememberMe
      );
      return session;
    },
    onSuccess: (session) => {
      if (flowType === LoginFlowType.SIGN_UP && !session.userId) {
        // User doesn't exist, need to register
        register();
        return;
      }
      handleAuthSuccess(
        session.emailConfirmed,
        session.profileConfirmed,
        session.accessToken
      );
    },
  });

  const handleAuthSuccess = (
    emailConfirmed: boolean,
    profileConfirmed: boolean,
    token: string
  ) => {
    if (!emailConfirmed) {
      navigate(PageRoutes.EMAIL_SIGNUP_VERIFY);
      return;
    }
    if (!profileConfirmed) {
      navigate(PageRoutes.ONBOARDING_BEST_DESCRIBES);
      return;
    }

    // If all checks pass, fetch profile and redirect
    getProfileAndRedirect(token, returnTo);
  };

  const title =
    flowType === LoginFlowType.SIGN_UP
      ? SignUpMessages.SIGN_UP
      : SignUpMessages.LOG_IN;
  const errorMessage =
    flowType === LoginFlowType.SIGN_UP
      ? SignUpErrorMessages.SIGN_UP
      : SignUpErrorMessages.LOG_IN;

  const onSubmit = () => {
    if (flowType === LoginFlowType.SIGN_UP) {
      resetLogin();
      register();
    } else {
      login();
    }
  };

  useEffect(() => {
    resetLogin();
    resetRegister();
  }, [flowType]);

  return (
    <LoginWrapper animate={false}>
      <form onSubmit={form.onSubmit(onSubmit)}>
        <MobileLogo />
        <Stack>
          <Group justify="space-between" align="center">
            <Title order={2} ta="left" data-testid={SignUpEmailTestIds.TITLE}>
              {title}
            </Title>
            <Anchor
              data-testid={SignUpEmailTestIds.BACK_BUTTON}
              c="dimmed"
              onClick={() => navigate(-1)}
            >
              Back
            </Anchor>
          </Group>
          <TextInput
            data-testid={SignUpEmailTestIds.EMAIL_INPUT}
            radius={'lg'}
            size="md"
            placeholder="Your email"
            {...form.getInputProps('email')}
          />
          <PasswordInput
            data-testid={SignUpEmailTestIds.PASSWORD_INPUT}
            radius={'lg'}
            size="md"
            placeholder="Your password"
            {...form.getInputProps('password')}
            key={form.key('password')}
          />
          {flowType === LoginFlowType.SIGN_UP && (
            <PasswordInput
              data-testid={SignUpEmailTestIds.CONFIRM_PASSWORD_INPUT}
              radius={'lg'}
              size="md"
              placeholder="Confirm password"
              {...form.getInputProps('confirmPassword')}
              key={form.key('confirmPassword')}
            />
          )}
          {flowType === LoginFlowType.LOG_IN && (
            <Checkbox
              data-testid={SignUpEmailTestIds.REMEMBER_ME_CHECKBOX}
              label="Keep me logged in"
              color={primaryColor}
              checked={rememberMe}
              onChange={(event) => setRememberMe(event.currentTarget.checked)}
            />
          )}
          {flowType === LoginFlowType.LOG_IN && (
            <Anchor
              data-testid={SignUpEmailTestIds.FORGOT_PASSWORD_LINK}
              size="sm"
              c="dimmed"
              ta="right"
              onClick={() => navigate(PageRoutes.FORGOT_PASSWORD, { state: { email: form.values.email } })}
            >
              Forgot password?
            </Anchor>
          )}
          <Button
            fullWidth
            data-testid={SignUpEmailTestIds.SUBMIT_BUTTON}
            color={primaryColor}
            c={'white'}
            radius={'lg'}
            size={'md'}
            type="submit"
            loading={isLoadingRegister || isLoadingLogin}
          >
            NEXT
          </Button>
          {(isRegisterError || isLoginError) && (
            <SignUpError message={errorMessage} />
          )}
          {flowType === LoginFlowType.SIGN_UP && (
            <HaveAnAccountAlreadyText returnTo={returnTo} />
          )}
        </Stack>
      </form>
    </LoginWrapper>
  );
};

const SignUpError = ({ message }: { message: string }) => {
  return (
    <Alert
      radius={'lg'}
      variant="filled"
      color="red"
      title="Error"
      icon={<IconInfoCircle />}
      data-testid={SignUpEmailTestIds.ERROR_MESSAGE}
    >
      {message}
    </Alert>
  );
};

export const MobileLogo = () => {
  return (
    <Flex
      hiddenFrom="md"
      justify={'center'}
      pos="absolute"
      top={20}
      left={0}
      right={0}
      style={{ zIndex: 10 }}
      data-testid={SignUpEmailTestIds.MOBILE_LOGO}
    >
      <Image src="/logo.png" alt="CircleBe Logo" maw={150} />
    </Flex>
  );
};

export enum SignUpEmailTestIds {
  MOBILE_LOGO = 'MOBILE_LOGO',
  ERROR_MESSAGE = 'ERROR_MESSAGE',
  TITLE = 'TITLE',
  BACK_BUTTON = 'BACK_BUTTON',
  EMAIL_INPUT = 'EMAIL_INPUT',
  PASSWORD_INPUT = 'PASSWORD_INPUT',
  CONFIRM_PASSWORD_INPUT = 'CONFIRM_PASSWORD_INPUT',
  SUBMIT_BUTTON = 'SUBMIT_BUTTON',
  REMEMBER_ME_CHECKBOX = 'REMEMBER_ME_CHECKBOX',
  FORGOT_PASSWORD_LINK = 'FORGOT_PASSWORD_LINK',
}

export enum SignUpMessages {
  SIGN_UP = 'Sign Up',
  LOG_IN = 'Log In',
}

export enum SignUpErrorMessages {
  SIGN_UP = 'Error registering email and password. If you already have an account, please log in instead.',
  LOG_IN = 'Error logging in with email and password.',
}

export enum LoginFlowType {
  SIGN_UP,
  LOG_IN,
}

export interface SignUpLoginProps {
  flowType: LoginFlowType;
}
